/**
 * Hook React pour l'intégration de l'IA dans ADHD Planner
 * Fournit une interface simple pour le parsing, les suggestions et la planification
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import aiService from '../services/aiService';

export function useAIService() {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [usageStats, setUsageStats] = useState(null);

  // Ref pour le debounce
  const debounceRef = useRef(null);

  // Initialisation au montage
  useEffect(() => {
    const init = async () => {
      try {
        setIsInitializing(true);
        const success = await aiService.init();
        setIsAvailable(success);

        if (success) {
          setUsageStats(aiService.getUsageStats());
        }
      } catch (err) {
        console.error('Erreur initialisation IA:', err);
        setError('Erreur initialisation du service IA');
        setIsAvailable(false);
      } finally {
        setIsInitializing(false);
      }
    };

    init();

    // Cleanup
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  /**
   * Parser une tâche depuis du texte naturel
   * @param {string} input - Texte à parser
   * @param {Object} context - Contexte (date, etc.)
   * @returns {Promise<Object|null>} Tâche parsée ou null
   */
  const parseTask = useCallback(async (input, context = {}) => {
    if (!input || input.trim().length < 3) {
      return null;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await aiService.parseNaturalLanguageTask(input, context);
      setUsageStats(aiService.getUsageStats());
      return result;
    } catch (err) {
      console.error('Erreur parsing IA:', err);
      setError(err.message || 'Erreur lors de l\'analyse');

      // Essayer le fallback local
      return aiService.localFallbackParse(input, context);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  /**
   * Parser avec debounce (pour les inputs en temps réel)
   * @param {string} input - Texte à parser
   * @param {Object} context - Contexte
   * @param {number} delay - Délai en ms (défaut 500)
   * @returns {Promise<Object|null>} Tâche parsée
   */
  const parseTaskDebounced = useCallback((input, context = {}, delay = 500) => {
    return new Promise((resolve) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        const result = await parseTask(input, context);
        resolve(result);
      }, delay);
    });
  }, [parseTask]);

  /**
   * Rafraîchir les suggestions contextuelles
   * @param {Object} context - Contexte (mood, energy, etc.)
   */
  const refreshSuggestions = useCallback(async (context = {}) => {
    if (!isAvailable && navigator.onLine) {
      // Pas de service IA, suggestions par défaut
      setSuggestions(aiService.getDefaultSuggestions(context));
      return;
    }

    try {
      const newSuggestions = await aiService.getSuggestions(context);
      setSuggestions(newSuggestions);
      setUsageStats(aiService.getUsageStats());
    } catch (err) {
      console.warn('Erreur suggestions IA:', err);
      // Fallback aux suggestions par défaut
      setSuggestions(aiService.getDefaultSuggestions(context));
    }
  }, [isAvailable]);

  /**
   * Planifier automatiquement une journée
   * @param {Array} tasks - Tâches à planifier
   * @param {Object} constraints - Contraintes (énergie, préférences)
   * @returns {Promise<Object>} Planning optimisé
   */
  const planDay = useCallback(async (tasks, constraints = {}) => {
    if (!tasks || tasks.length === 0) {
      return { schedule: [], warnings: [] };
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await aiService.generateOptimalSchedule(tasks, constraints);
      setUsageStats(aiService.getUsageStats());
      return result;
    } catch (err) {
      console.error('Erreur planification IA:', err);
      setError(err.message || 'Erreur lors de la planification');

      // Fallback local
      return aiService.localScheduleOptimization(tasks, constraints);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  /**
   * Fermer/réinitialiser l'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Appliquer une suggestion de tâche
   * @param {Object} suggestion - Suggestion sélectionnée
   * @returns {Object|null} Tâche prête à ajouter
   */
  const applySuggestion = useCallback((suggestion) => {
    if (!suggestion) return null;

    // Si la suggestion contient une tâche pré-définie
    if (suggestion.task) {
      return {
        ...suggestion.task,
        isCustom: true,
        iconName: aiService.getIconForCategory(suggestion.task.category)
      };
    }

    return null;
  }, []);

  /**
   * Générer des sous-tâches intelligentes pour une tâche
   * @param {Object} task - La tâche à décomposer
   * @param {Object} context - Contexte (mood, energy)
   * @returns {Promise<Object>} Sous-tâches générées
   */
  const generateSubTasks = useCallback(async (task, context = {}) => {
    if (!task) return null;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await aiService.generateSubTasks(task, context);
      setUsageStats(aiService.getUsageStats());
      return result;
    } catch (err) {
      console.error('Erreur génération sous-tâches:', err);
      setError(err.message || 'Erreur lors de la génération');
      // Fallback aux sous-tâches par défaut
      return aiService.getDefaultSubTasks(task);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  /**
   * Vérifier si le service est limité (quota atteint)
   */
  const isLimited = useCallback(() => {
    if (!usageStats) return false;
    return usageStats.percentUsed >= 90;
  }, [usageStats]);

  return {
    // États
    isInitializing,
    isAvailable,
    isProcessing,
    error,
    suggestions,
    usageStats,

    // Actions de parsing
    parseTask,
    parseTaskDebounced,

    // Actions de suggestions
    refreshSuggestions,
    applySuggestion,

    // Planification
    planDay,

    // Sous-tâches
    generateSubTasks,

    // Utilitaires
    clearError,
    isLimited,

    // Accès direct au service (usage avancé)
    service: aiService
  };
}

export default useAIService;
